import { Box, Divider, Drawer, Stack, useMediaQuery } from '@mui/material';
import { listClusters } from '@qdrant/qdrant-cloud-public-api/cluster/v1/connect-query';
import { Link } from '@tanstack/react-router';
import { memo, useRef } from 'react';
import { WithRpcPermissions } from '@/components/AccessManagement/WithRpcPermissions/WithRpcPermissions';
import { Scrollbar, type ScrollbarRef } from '@/components/Common/Scrollbar';
import { Logo } from '@/components/Logo';
import { useSafeAccountId } from '@/routes/_authenticated/_dashboard/accounts/$accountId/route.hooks';
import { DASHBOARD_SIDEBAR_WIDTH } from './DashboardAppBar';
import { DashboardSidebarNavigation } from './DashboardSidebarNavigation';

export const DashboardSidebar = memo(function DashboardSidebar({
  onClose,
  open,
}: {
  onClose: () => void;
  open: boolean;
}) {
  const accountId = useSafeAccountId();
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up('lg'), { noSsr: true });
  const scrollbarRef = useRef<ScrollbarRef>(null);

  return (
    <Drawer
      anchor="left"
      slotProps={{
        paper: {
          elevation: 1,
          sx: {
            width: DASHBOARD_SIDEBAR_WIDTH,
          },
        },
      }}
      open={lgUp || open}
      onClose={lgUp ? undefined : onClose}
      variant={lgUp ? 'permanent' : 'temporary'}
      sx={lgUp ? undefined : { zIndex: (theme) => theme.zIndex.appBar + 100 }}
    >
      <Scrollbar
        ariaLabel="Sidebar"
        ref={scrollbarRef}
        sx={{
          height: '100%',
          '& .simplebar-content': {
            height: '100%',
          },
        }}
      >
        <Stack
          direction="column"
          sx={{
            height: '100%',
          }}
        >
          {accountId ? (
            <WithRpcPermissions
              methods={[listClusters]}
              show="always"
              render={({ matches }) => (
                <Link to={matches ? '/accounts/$accountId/clusters' : '/get-started'} params={{ accountId }}>
                  <LogoBox />
                </Link>
              )}
            />
          ) : (
            <Link to="/preferences">
              <LogoBox />
            </Link>
          )}
          <Divider />
          <Box component="nav" sx={{ flexGrow: 1 }} role="navigation" aria-label="Main">
            <DashboardSidebarNavigation scrollbarRef={scrollbarRef} />
          </Box>
        </Stack>
      </Scrollbar>
    </Drawer>
  );
});

const LogoBox = memo(() => (
  <Box sx={{ my: 2, mx: 3, height: (theme) => theme.spacing(4), textAlign: 'left' }}>
    <Logo width="auto" />
  </Box>
));
